import { injectable } from 'tsyringe';
import { NoRecommendationReasons } from '../no-recommendations-reasons.enums';
import { PersistentStorageService } from '../../persistent-storage-service/persistent-storage.service';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import {
  conditionType,
  ConditionType,
} from './decrators/condition-type.decorator';
import { LoggerService } from '../../../../shared/services/logger.service';
import { UserActionService } from '../../user-action.service';
import { IUserActionServiceDelegate } from '../../../../shared/interfaces/user-action-service.interface';
import { PromotionDisplayDetails } from '../../../../shared/models/promotion-display-details.model';
import { Game } from '../../../models/game.model';
import { PromotionType } from '../../datastore-service/models/raw-promotion.model';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Promotion)
export class ConditionPromotionOfferApproved
  implements ICondition, IUserActionServiceDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionPromotionOfferApproved',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly persistentLocalStorage: PersistentStorageService,
    private readonly userActionService: UserActionService,
  ) {
    this.userActionService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  shouldSkip(context: ConditionContext) {
    const { promotion } = context;

    if (!promotion?.type || promotion.type !== PromotionType.Offer) {
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { game, promotion } = context;

    const approvedTimestamp = this.getApplicationApprovedTimestamp(game);

    if (!approvedTimestamp || approvedTimestamp === 0) {
      return [true];
    }

    this.logger.info(`${promotion?.name} was previously approved by the user.`);
    return [false, NoRecommendationReasons.ApprovedByUser];
  }

  // ---------------------------------------------------------------------------
  private getApplicationApprovedTimestamp(game: Game): number {
    const displayedPromotion =
      this.persistentLocalStorage.getDisplayedPromotionOfferInfo(game);

    return displayedPromotion?.approvedTimestamp ?? 0;
  }

  // ---------------------------------------------------------------------------
  // IUserActionServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onUserActionApproved(activePromotion: PromotionDisplayDetails) {
    const { promotionId, gameId, promotionType } = activePromotion;

    if (promotionType !== PromotionType.Offer) {
      return;
    }

    this.logger.info(`The promotion (${gameId}, ${promotionId}) was Approved.`);

    this.persistentLocalStorage.setPromotionApproved(gameId);
  }
}
